'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import { RootState } from '@/store';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getUi, hideDrawerState } from '@/store/reducers/ui-slice';
import { AdminRoles } from '@alien-platform/bo-sdk-v2';
import { useColorScheme } from '@mui/material';
import { useSelector } from 'react-redux';

import { GeneralDrawer } from '@/types/general';
import type { Settings } from '@/types/settings';
import { setSettings as setPersistedSettings } from '@/lib/settings/set-settings';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useSettings } from '@/hooks/use-settings';
import type { Mode } from '@/styles/theme/types';

import DrawerTemplate from './drawer-template';
import { SettingsDrawer } from './settings/settings-drawer';

export default function AppearanceDrawer(): React.JSX.Element {
  const { settings } = useSettings();
  const { mode, setMode } = useColorScheme();
  const router = useRouter();
  const { user } = useSelector((state: RootState) => state.user);
  const roles = user?.role;

  const [openDrawer, setOpenDrawer] = React.useState<boolean>(false);
  const mdDown = useMediaQuery('down', 'md');

  const { drawerState } = useAppSelector(getUi);
  const dispatch = useAppDispatch();

  const handleUpdate = async (values: Partial<Settings> & { mode?: Mode }): Promise<void> => {
    const { mode: newMode, ...other } = values;

    if (newMode) {
      setMode(newMode);
    }

    const updatedSettings = { ...settings, ...other } satisfies Settings;

    await setPersistedSettings(updatedSettings);

    // Refresh the router to apply the new settings.
    router.refresh();
  };

  const handleReset = async (): Promise<void> => {
    setMode(null);

    await setPersistedSettings({});

    // Refresh the router to apply the new settings.
    router.refresh();
  };

  if (mdDown || !roles?.includes(AdminRoles.SUPER_ADMIN)) return <></>;

  return (
    <DrawerTemplate
      open={drawerState.APPEARANCE}
      onClose={() => dispatch(hideDrawerState(GeneralDrawer.APPEARANCE))}
      title="Appearance Settings"
    >
      <SettingsDrawer
        onClose={() => {
          setOpenDrawer(false);
        }}
        onReset={handleReset}
        onUpdate={handleUpdate}
        open={openDrawer}
        values={{ ...settings, mode }}
      />
    </DrawerTemplate>
  );
}
